import React, { useState, useEffect } from 'react';
import { costiEsterniAPI, commesseAPI } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  Package,
  Building2,
  Wrench,
  Server,
  Receipt,
  Loader2,
  Trash2,
  Pencil,
  AlertCircle,
  FileText
} from 'lucide-react';

const CATEGORIA_COSTO_OPTIONS = [
  { value: 'consulenza', label: 'Consulenza', icon: Building2 },
  { value: 'licenze', label: 'Licenze SW', icon: Server },
  { value: 'materiali', label: 'Materiali', icon: Package },
  { value: 'servizi', label: 'Servizi', icon: Wrench },
  { value: 'altro', label: 'Altro', icon: Receipt },
];

const getCategoriaIcon = (categoria) => {
  const opt = CATEGORIA_COSTO_OPTIONS.find(o => o.value === categoria);
  return opt?.icon || Receipt;
};

export const CostiEsterniPage = () => {
  const { user } = useAuth();
  const [costiEsterni, setCostiEsterni] = useState([]);
  const [commesseDisponibili, setCommesseDisponibili] = useState([]);
  const [selectedCommessa, setSelectedCommessa] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [budgetInfo, setBudgetInfo] = useState({});

  const [formData, setFormData] = useState({
    data: '',
    commessa_id: '',
    categoria: 'consulenza',
    fornitore: '',
    importo: '',
    descrizione: '',
    fattura_riferimento: ''
  });

  const canAccess = user?.ruolo === 'admin' || user?.ruolo === 'coordinator' || user?.ruolo === 'pm';

  useEffect(() => {
    if (canAccess) {
      loadData();
    }
  }, [canAccess, selectedCommessa]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Carica commesse con budget costi esterni
      const commesseRes = await commesseAPI.getAll();
      const commesseConBudget = commesseRes.data.filter(c => {
        const budgetEsterni = c.budget_costi_esterni || [];
        const totale = budgetEsterni.reduce((acc, b) => acc + (b.costo_previsto || 0), 0);
        return totale > 0 && c.stato === 'attiva';
      });
      setCommesseDisponibili(commesseConBudget);

      // Carica costi esterni
      const params = selectedCommessa && selectedCommessa !== 'all' ? { commessa_id: selectedCommessa } : {};
      const costiRes = await costiEsterniAPI.getAll(params);
      setCostiEsterni(costiRes.data);
    } catch (error) {
      console.error('Error loading costi esterni:', error);
      toast.error('Errore nel caricamento dei costi esterni');
    } finally {
      setIsLoading(false);
    }
  };

  const loadBudgetInfo = async (commessaId) => {
    if (!commessaId) return;
    try {
      const res = await costiEsterniAPI.getDisponibili(commessaId);
      setBudgetInfo(prev => ({ ...prev, [commessaId]: res.data }));
    } catch (error) {
      console.error('Error loading budget info:', error);
    }
  };

  const openAddDialog = () => {
    setEditingEntry(null);
    setFormData({
      data: new Date().toISOString().split('T')[0],
      commessa_id: selectedCommessa && selectedCommessa !== 'all' ? selectedCommessa : '',
      categoria: 'consulenza',
      fornitore: '',
      importo: '',
      descrizione: '',
      fattura_riferimento: ''
    });
    if (selectedCommessa) {
      loadBudgetInfo(selectedCommessa);
    }
    setIsDialogOpen(true);
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      data: entry.data,
      commessa_id: entry.commessa_id,
      categoria: entry.categoria,
      fornitore: entry.fornitore || '',
      importo: entry.importo.toString(),
      descrizione: entry.descrizione || '',
      fattura_riferimento: entry.fattura_riferimento || ''
    });
    loadBudgetInfo(entry.commessa_id);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.commessa_id || !formData.importo) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        data: formData.data,
        commessa_id: formData.commessa_id,
        categoria: formData.categoria,
        fornitore: formData.fornitore || null,
        importo: parseFloat(formData.importo),
        descrizione: formData.descrizione || null,
        fattura_riferimento: formData.fattura_riferimento || null
      };

      if (editingEntry) {
        await costiEsterniAPI.update(editingEntry.id, submitData);
        toast.success('Costo esterno aggiornato');
      } else {
        await costiEsterniAPI.create(submitData);
        toast.success('Costo esterno inserito');
      }

      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      console.error('Error saving costo esterno:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nel salvataggio');
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo costo?')) return;
    
    try {
      await costiEsterniAPI.delete(entryId);
      toast.success('Costo esterno eliminato');
      loadData();
    } catch (error) {
      console.error('Error deleting costo:', error);
      const detail = error.response?.data?.detail;
      const errorMsg = Array.isArray(detail) 
        ? detail.map(e => e.msg || e.message).join(', ') 
        : (typeof detail === 'string' ? detail : 'Errore nell\'eliminazione');
      toast.error(errorMsg);
    }
  };

  if (!canAccess) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Non hai i permessi per visualizzare questa pagina</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6" data-testid="costi-esterni-loading">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  const currentBudgetInfo = budgetInfo[formData.commessa_id];
  const totaleConsuntivo = costiEsterni.reduce((acc, c) => acc + (c.importo || 0), 0);

  return (
    <div className="space-y-6" data-testid="costi-esterni-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Costi Esterni</h1>
          <p className="text-muted-foreground">Rendiconta i costi esterni per commessa (consulenze, licenze, materiali)</p>
        </div>
        {commesseDisponibili.length > 0 && (
          <Button onClick={openAddDialog} data-testid="add-costo-esterno-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Costo Esterno
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Label htmlFor="filter-commessa" className="mb-2 block">Filtra per Commessa</Label>
              <Select
                value={selectedCommessa}
                onValueChange={setSelectedCommessa}
              >
                <SelectTrigger id="filter-commessa" data-testid="filter-commessa-select">
                  <SelectValue placeholder="Tutte le commesse" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte le commesse</SelectItem>
                  {commesseDisponibili.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_commessa} - {c.nome_progetto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm text-muted-foreground">Totale Consuntivo</p>
                <p className="text-2xl font-bold font-mono">{formatCurrency(totaleConsuntivo)}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {commesseDisponibili.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              Nessuna commessa con budget costi esterni disponibile
            </p>
          ) : costiEsterni.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">
              Nessun costo esterno registrato
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Commessa</TableHead>
                  <TableHead>Categoria</TableHead>
                  <TableHead>Fornitore</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead className="text-right">Importo</TableHead>
                  <TableHead>Stato</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {costiEsterni.map((entry) => {
                  const CatIcon = getCategoriaIcon(entry.categoria);
                  return (
                    <TableRow key={entry.id} data-testid={`costo-row-${entry.id}`}>
                      <TableCell className="font-mono text-sm">{entry.data}</TableCell>
                      <TableCell>
                        <div>
                          <span className="font-mono text-xs text-muted-foreground">{entry.commessa_codice}</span>
                          <p className="text-sm font-medium">{entry.commessa_nome}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2 capitalize">
                          <CatIcon className="h-4 w-4 text-muted-foreground" />
                          {entry.categoria}
                        </span>
                      </TableCell>
                      <TableCell>{entry.fornitore || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate">{entry.descrizione || '-'}</TableCell>
                      <TableCell className="text-right font-mono font-bold">
                        {formatCurrency(entry.importo)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          entry.stato === 'approvato' ? 'default' : 
                          entry.stato === 'respinto' ? 'destructive' : 'secondary'
                        }>
                          {entry.stato === 'approvato' ? 'Approvato' : 
                           entry.stato === 'respinto' ? 'Respinto' : 'In attesa'}
                        </Badge>
                        {entry.motivo_rifiuto && (
                          <p className="text-xs text-red-500 mt-1">{entry.motivo_rifiuto}</p>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {entry.stato !== 'approvato' && (
                          <div className="flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openEditDialog(entry)}
                              data-testid={`edit-costo-${entry.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDelete(entry.id)}
                              data-testid={`delete-costo-${entry.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="costo-esterno-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingEntry ? 'Modifica Costo Esterno' : 'Nuovo Costo Esterno'}
            </DialogTitle>
            <DialogDescription>
              {editingEntry ? 'Modifica i dati del costo' : 'Inserisci i dati del costo esterno'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="costo-data">Data *</Label>
                <Input
                  id="costo-data"
                  type="date"
                  value={formData.data}
                  onChange={(e) => setFormData(prev => ({ ...prev, data: e.target.value }))}
                  required
                  data-testid="costo-data-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="costo-importo">Importo (€) *</Label>
                <Input
                  id="costo-importo"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.importo}
                  onChange={(e) => setFormData(prev => ({ ...prev, importo: e.target.value }))}
                  placeholder="0.00"
                  required
                  data-testid="costo-importo-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="costo-commessa">Commessa *</Label>
              <Select
                value={formData.commessa_id}
                onValueChange={(value) => {
                  setFormData(prev => ({ ...prev, commessa_id: value }));
                  loadBudgetInfo(value);
                }}
              >
                <SelectTrigger id="costo-commessa" data-testid="costo-commessa-select">
                  <SelectValue placeholder="Seleziona commessa" />
                </SelectTrigger>
                <SelectContent>
                  {commesseDisponibili.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_commessa} - {c.nome_progetto}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {currentBudgetInfo && (
                <div className="text-xs mt-2 p-2 bg-muted rounded">
                  <div className="flex justify-between">
                    <span>Budget: {formatCurrency(currentBudgetInfo.budget_totale)}</span>
                    <span>Utilizzato: {formatCurrency(currentBudgetInfo.consuntivo_totale)}</span>
                  </div>
                  <Progress 
                    value={currentBudgetInfo.budget_totale > 0 
                      ? (currentBudgetInfo.consuntivo_totale / currentBudgetInfo.budget_totale * 100) 
                      : 0
                    } 
                    className="h-1.5 mt-2"
                  />
                  <p className="mt-1">
                    Disponibile: <span className={
                      currentBudgetInfo.disponibile >= 0 ? 'text-emerald-600' : 'text-red-600'
                    }>{formatCurrency(currentBudgetInfo.disponibile)}</span>
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="costo-categoria">Categoria *</Label>
                <Select
                  value={formData.categoria}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, categoria: value }))}
                >
                  <SelectTrigger id="costo-categoria" data-testid="costo-categoria-select">
                    <SelectValue placeholder="Seleziona categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIA_COSTO_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <span className="flex items-center gap-2">
                          <opt.icon className="h-4 w-4" />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="costo-fornitore">Fornitore</Label>
                <Input
                  id="costo-fornitore"
                  value={formData.fornitore}
                  onChange={(e) => setFormData(prev => ({ ...prev, fornitore: e.target.value }))}
                  placeholder="Nome fornitore"
                  data-testid="costo-fornitore-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="costo-fattura">Rif. Fattura</Label>
              <Input
                id="costo-fattura"
                value={formData.fattura_riferimento}
                onChange={(e) => setFormData(prev => ({ ...prev, fattura_riferimento: e.target.value }))}
                placeholder="Numero fattura o riferimento"
                data-testid="costo-fattura-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="costo-descrizione">Descrizione</Label>
              <Textarea
                id="costo-descrizione"
                value={formData.descrizione}
                onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                placeholder="Dettagli del costo..."
                rows={2}
                data-testid="costo-descrizione-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Annulla
              </Button>
              <Button type="submit" disabled={isSubmitting} data-testid="costo-submit-btn">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  editingEntry ? 'Salva Modifiche' : 'Registra Costo'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CostiEsterniPage;
